import { createPinia, defineStore, storeToRefs } from "pinia";
import axios from "axios";
import { ref, watch, onMounted, compile, toRaw, getCurrentInstance } from "vue";
import { useVueErd } from "@niftywell/vue-mvx";
import {
  TokenPayment,
  Transaction,
  TransactionPayload,
  AbiRegistry,
  ResultsParser,
  SmartContract,
  ContractFunction,
  AddressValue,
  Address,
  U64Value,
  TokenIdentifierValue,
  BigUIntValue,
} from "@multiversx/sdk-core";
import abi_json from "@/assets/ABI/nifty-maze.json";
import type { Account, ITransactionOnNetwork } from "@multiversx/sdk-core";
import { intToHex, sleep, strToHex, formatMultiEsdtData } from "@/ts/utils";
import "vue3-toastify/dist/index.css";
import type {
  Nft,
  NftType,
  IndividualNfts,
  Balance,
  UnbondingNft,
  IndividualForPacks,
} from "@/types";
import Msg from "@/store/MsgMaze.vue";
import type { Id } from "vue3-toastify";
import raw_metadata from "@/assets/metadata/metadata.json";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

export interface MetadataItem {
  rank: number; // Add other properties as needed, like 'id', 'name', etc.
}
const useMazeStore = defineStore("mazeStore", () => {
  const { account, erd, fetchAccount } = useVueErd();
  const contract = ref<SmartContract>();
  const abi = ref();
  const abiRegistry = ref();
  const contract_address =
    "erd1qqqqqqqqqqqqqpgq702juwfdrj76vze0pat5ssktlg50avv83agsc0jyy8";
  const sender_account = ref<Account>();

  // Transaction info
  const transactionState = ref();
  const transactionResult = ref();
  const transactionUrl = ref();

  const active_toast = ref();
  const toast_info = ref();
  const toast_id = ref<Id>();
  const maze = ref();
  const player = ref();
  const payment_token = ref();
  const cooldown = ref();
  const last_move_timestamp = ref();
  const time_remaining = ref(0);
  const collected_keys = ref<any[]>([]);
  const gameStatus = ref();
  const collectedTokens = ref();
  const prizes_to_win = ref<any[]>([]);
  const player_moves = ref<any[]>([]);
  const mvp_claimed = ref();
  const mvp = ref({
    amount: 0,
    address: "",
    full_address: "",
  });
  const claim_test = ref();
  const transactions = ref([]);
  onMounted(async () => {
    erd.on("transaction", async (transaction: ITransactionOnNetwork) => {
      transactionUrl.value =
        "https://explorer.elrond.com/transactions/" + transaction.hash;
      let returnCode = null;
      try {
        returnCode = await new ResultsParser().parseUntypedOutcome(transaction);
      } catch (e) {
        console.log(e);
      }
      if (returnCode != null && returnCode.returnCode.isSuccess()) {
        console.log("TX SUCCESS");
        toast_info.value = {
          title: "Transaction success!",
          message: "Your transaction was processed.",
          status: returnCode.returnCode.isSuccess(),
          link: transactionUrl.value,
        };
        toast.update(toast_id.value!, {
          autoClose: 20000,
          type: "success",
          isLoading: false,
        });
        reset();
      } else if (returnCode != null) {
        toast_info.value = {
          title: "Transaction failed!",
          message: returnCode.returnMessage,
          status: returnCode.returnCode.isSuccess(),
          link: transactionUrl.value,
        };
        toast.update(toast_id.value!, {
          autoClose: 20000,
          type: "error",
          isLoading: false,
        });
      } else {
        toast_info.value = {
          title: "Something Happend!",
          message: "Check your transaction.",
          status: null,
          link: transactionUrl.value,
        };
        toast.update(toast_id.value!, {
          autoClose: 20000,
          type: "info",
          isLoading: false,
        });
      }
      sleep(3000);
      reset();
    });
    sender_account.value = await fetchAccount();
  });
  function wipe() {
    // Transaction info
    transactionState.value = undefined;
    transactionResult.value = undefined;
    transactionUrl.value = undefined;
  }
  const reset = async () => {
    wipe();
    await init();
  };

  function initTxToaster() {
    toast_info.value = {
      title: "Transaction pending!",
      message: "Processing your transaction.",
      status: null,
      link: transactionUrl.value,
    };
    toast_id.value = toast(Msg, {
      autoClose: true,
      closeOnClick: false,
      closeButton: true,
      type: "loading",
      isLoading: true,
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  }

  const init = async () => {
    erd.proxy.config.timeout = 15000;
    setContract();
    await getMaze();
    await getPaymentToken();
    await getMvp();
    await getGeneralData();
    await getTransaction();
    await getClaimedTest();
    //console.log("Token info:");
    //console.log(payment_token.value);
    sender_account.value = await fetchAccount();
  };

  const setContract = () => {
    if (abi.value == null) {
      abiRegistry.value = AbiRegistry.create(abi_json);
    }
    contract.value = new SmartContract({
      address: new Address(contract_address),
      abi: abiRegistry.value,
    });
  };

  async function getPaymentToken() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getPaymentInfo"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getPaymentInfo")
      );
      if (result.returnCode.isSuccess()) {
        const res = result.values[0].valueOf();
        payment_token.value = {
          amount: parseFloat(res.amount.valueOf()) * 10 ** -18,
          nonce: parseInt(res.nonce.valueOf()),
          ticker: res.ticker.valueOf(),
        };
      }
    }
  }

  async function getLastMoveTimestamp() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getLastMoveTimestamp"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getLastMoveTimestamp")
      );
      if (result.returnCode.isSuccess()) {
        const res = result.values[0].valueOf();
        last_move_timestamp.value = res.valueOf();
        time_remaining.value = res.valueOf();
      }
    }
  }

  async function getCoolDown() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getCoolDown"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getCoolDown")
      );
      if (result.returnCode.isSuccess()) {
        const res = result.values[0].valueOf();
        cooldown.value = res.valueOf();
      }
    }
  }

  async function getMaze() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getFullMatrix"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getFullMatrix")
      );
      if (result.returnCode.isSuccess()) {
        const temp = result.values[0].valueOf();
        const dimensions = temp[temp.length - 1].valueOf();
        const rows = dimensions.fields[0].valueOf();
        const cols = dimensions.fields[1].valueOf();
        const matrix: any = [];
        for (let i = 0; i < rows; i++) {
          matrix[i] = [];
          for (let j = 0; j < cols; j++) {
            // Calculate the index in temp
            const index = i * cols + j;
            matrix[i][j] = temp[index];
          }
        }

        maze.value = matrix;
        return maze.value;
      }
    }
  }

  async function getPlayerPosition() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getPlayerPosition"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getPlayerPosition")
      );
      if (result.returnCode.isSuccess()) {
        player.value = result.values[0].valueOf();
        return player.value;
      }
    }
  }

  async function getClaimedTest() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getTestClaims"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getTestClaims")
      );
      if (result.returnCode.isSuccess()) {
        const claim_test_raw = result.values[0].valueOf();
        const isPresentInArray = claim_test_raw.some(
          (address: any) => address.bech32() === account.address?.bech32()
        );
        //console.log("Is present:", isPresentInArray);
        claim_test.value = isPresentInArray;
        return claim_test.value;
      }
    }
  }

  async function getGameStatus() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getGameStatus"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getGameStatus")
      );
      if (result.returnCode.isSuccess()) {
        gameStatus.value = result.values[0].valueOf();
        return gameStatus.value;
      }
    }
  }

  async function getCollectedTokens() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getCollectedTokens"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getCollectedTokens")
      );
      if (result.returnCode.isSuccess()) {
        collectedTokens.value =
          parseFloat(result.values[0].valueOf()) * 10 ** -18;
        //console.log("Collected: ", collectedTokens.value);
        return collectedTokens.value;
      }
    }
  }

  async function getMvp() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getMvp"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getMvp")
      );
      if (result.returnCode.isSuccess()) {
        const mvp_info = result.values[0].valueOf();
        mvp.value.amount = parseFloat(mvp_info.amount.valueOf()) * 10 ** -18;
        mvp.value.full_address = mvp_info.address.bech32();
        const address =
          mvp.value.full_address.substring(0, 4) +
          "..." +
          mvp.value.full_address.substring(mvp.value.full_address.length - 4);
        await axios
          .get(`${erd.providers.api.url}/accounts/${mvp.value.full_address}`)
          .then((res) => {
            if (res.data.username) {
              mvp.value.address = res.data.username.split(".")[0];
            } else {
              mvp.value.address = address;
            }
          })
          .catch((err) => console.log(err));
        return mvp.value;
      }
    }
  }

  async function getKeys() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getKeys"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getKeys")
      );
      if (result.returnCode.isSuccess()) {
        collected_keys.value = result.values[0].valueOf();
        return collected_keys.value;
      }
    }
  }

  async function getGeneralData() {
    if (account.logged() && account.address !== undefined) {
      const address = new AddressValue(account.address);
      const query = contract.value!.createQuery({
        func: new ContractFunction("getGeneralData"),
      });
      const response = await erd.providers.proxy.queryContract(query);
      const resultParser = new ResultsParser();
      const result = resultParser.parseQueryResponse(
        response,
        contract.value!.getEndpoint("getGeneralData")
      );
      if (result.returnCode.isSuccess()) {
        console.log("result is here for general data:", result);
        prizes_to_win.value = [];
        player_moves.value = [];
        let final_move = {};
        collected_keys.value = [];
        let raw_move;
        result.values[0].valueOf().forEach((data: any) => {
          switch (data.name) {
            case "TypePosition":
              // Code to run when myString is "TypePosition"
              player.value = data.fields[0].valueOf();
              //console.log("player position:", player.value);
              break;
            case "TypeGameStatus":
              // Code to run when myString is "TypeGameStatus"
              gameStatus.value = data.fields[0].valueOf();
              break;
            case "TypeCooldown":
              // Code to run when myString is "TypeCooldown"
              cooldown.value = data.fields[0].valueOf();
              break;
            case "TypeLastMoveTimeStamp":
              // Code to run when myString is "TypeLastMoveTimeStamp"
              //last_move_timestamp.value = data.fields[0].valueOf();
              if (time_remaining.value != data.fields[0].valueOf()) {
                time_remaining.value = data.fields[0].valueOf();
              }
              //console.log("lastMove:", last_move_timestamp.value);
              break;
            case "TypeCollectedTokens":
              // Code to run when myString is "TypeCollectedTokens"
              collectedTokens.value =
                parseFloat(data.fields[0].valueOf()) * 10 ** -18;
              break;
            case "TypeKey":
              // Code to run when myString is "TypeCollectedTokens"
              collected_keys.value.push(data.fields[0].valueOf());
              //console.log("Collected keys:", collected_keys.value);
              break;
            case "TypePrize":
              // Code to run when myString is "TypeCollectedTokens"
              prizes_to_win.value.push(data.fields[0].valueOf());
              //console.log("Prizes to win:", prizes_to_win.value);
              break;
            case "TypePlayerMove":
              // Code to run when myString is "TypeCollectedTokens"
              raw_move = data.fields[0].valueOf();
              console.log("the raw move:", raw_move);
              final_move = {
                address:
                  raw_move.address.bech32().slice(0, 4) +
                  "..." +
                  raw_move.address.bech32().slice(-4),
                jumps: (
                  (parseFloat(raw_move.payment_amount.valueOf()) * 10 ** -18) /
                  payment_token.value.amount
                ).toFixed(0),
                //jumps: raw_move[0].payment_amount.c[0] * 0.01,
                move: raw_move.player_move.name,
              };
              player_moves.value.push(final_move);
              console.log("player_moves:", player_moves.value);
              break;
            case "TypeMvpClaimed":
              // Code to run when myString is "TypeCollectedTokens"
              mvp_claimed.value = data.fields[0].valueOf();
              //console.log("mvp_claimed:", mvp_claimed.value);
              break;
            default:
              //console.log("Unknown type");
              break;
          }
        });
      }
    }
  }

  function base64ToString(data: any) {
    return Buffer.from(data, "base64").toString("ascii");
  }

  function hexToDecimal(hex: any) {
    return parseInt(hex, 16);
  }

  function hexToString(hex: any) {
    let str = "";

    for (let i = 0; i < hex.length; i += 2) {
      const v = parseInt(hex.substr(i, 2), 16);
      if (v) str += String.fromCharCode(v);
    }

    return str;
  }

  function processTransaction(transaction: any) {
    if (["addMove", "mvpClaim"].includes(transaction.function)) {
      const sender = transaction.sender;
      let name = "";
      if (transaction.senderAssets) {
        name = transaction.senderAssets.name;
      }

      let data = base64ToString(transaction.data);
      const dataParts = data.split("@");
      const directionMapping = {
        "01": "MOVE UP",
        "02": "MOVE RIGHT",
        "03": "MOVE DOWN",
        "04": "MOVE LEFT",
      };
      data =
        dataParts[dataParts.length - 1] in directionMapping
          ? directionMapping[
              dataParts[dataParts.length - 1] as "01" | "02" | "03" | "04"
            ]
          : data;

      let result = "Ok";
      for (let i = 0; i < transaction.results.length; i++) {
        const currentResult = base64ToString(transaction.results[i].data);
        const resultParts = currentResult.split("@");

        if (currentResult.includes("ESDTNFTTransfer")) {
          result =
            hexToString(resultParts[1]) +
            "-" +
            hexToDecimal(resultParts[2]).toString();
          break;
        } else if (currentResult.includes("ESDTTransfer")) {
          result =
            (hexToDecimal(resultParts[2]) * 10 ** -18).toFixed(2) + "NFTBITS";
          break;
        }
      }

      const hash = transaction.txHash;

      // Ensure the timestamp is in Unix format (seconds since the epoch)
      const timestamp_date = new Date(transaction.timestamp * 1000); //.toLocaleTimeString();
      const timestamp = new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(timestamp_date);

      return { sender, data, result, hash, timestamp, name };
    }
    return null;
  }

  function processWinningAddresses(transaction: any) {
    if (["addMove", "mvpClaim"].includes(transaction.function)) {
      const sender = transaction.senderAssets
        ? transaction.senderAssets.name
        : transaction.sender.slice(0, 4) + "..." + transaction.sender.slice(-4);

      let data = base64ToString(transaction.data);
      const dataParts = data.split("@");
      const directionMapping = {
        "01": "MOVE UP",
        "02": "MOVE RIGHT",
        "03": "MOVE DOWN",
        "04": "MOVE LEFT",
      };
      data =
        dataParts[dataParts.length - 1] in directionMapping
          ? directionMapping[
              dataParts[dataParts.length - 1] as "01" | "02" | "03" | "04"
            ]
          : data;

      let result = "Ok";
      let pingAddress = "";
      let contains_ping = false;
      let winning_address = "";
      let final_start = "";
      let final_end = "";
      let final_winner = "";
      for (let i = 0; i < transaction.results.length; i++) {
        const currentResult = base64ToString(transaction.results[i].data);
        const resultParts = currentResult.split("@");

        // Check for "ping" in resultParts
        if (currentResult.includes("ping") || currentResult.includes("pong")) {
          // Extract the address from the last resultPart
          contains_ping = true;
        }
        if (contains_ping) {
          pingAddress = resultParts[resultParts.length - 1];
          if (pingAddress.length == 64) {
            winning_address = new Address(pingAddress).bech32();

            const timestamp_start_date = new Date(transaction.timestamp * 1000); //.toLocaleTimeString();
            const timestamp_start = new Intl.DateTimeFormat("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            }).format(timestamp_start_date);

            const timestamp_end_date = new Date(
              transaction.results[i].timestamp * 1000
            );
            const timestamp_end = new Intl.DateTimeFormat("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            }).format(timestamp_end_date);

            // Return additional pingAddress
            final_start = timestamp_start;
            final_end = timestamp_end;
            final_winner = winning_address;
          }
        }
      }

      if (contains_ping) {
        for (let i = 0; i < transaction.results.length; i++) {
          const currentResult = base64ToString(transaction.results[i].data);
          const resultParts = currentResult.split("@");

          if (currentResult.includes("ESDTNFTTransfer")) {
            result =
              hexToString(resultParts[1]) +
              "-" +
              hexToDecimal(resultParts[2]).toString();
          } else if (currentResult.includes("ESDTTransfer")) {
            result =
              (hexToDecimal(resultParts[2]) * 10 ** -18).toFixed(2) + "NFTBITS";
          }
        }
      }

      if (final_start != "") {
        return { final_start, final_end, final_winner, result };
      }
      return null;
    }
  }

  function filterTransactions(winners: any, processedTransactions: any) {
    // Converts a 'HH:MM:SS' time to a Unix timestamp
    function timeToUnix(time: any) {
      const date = new Date();
      const parts = time.split(":");
      date.setHours(parts[0]);
      date.setMinutes(parts[1]);
      date.setSeconds(parts[2]);
      return date.getTime() / 1000;
    }

    // Filter transactions
    return processedTransactions.filter((transaction: any) => {
      // Convert the transaction's timestamp to Unix format
      const transactionTime = timeToUnix(transaction.timestamp);

      // Check each winner
      for (const winner of winners) {
        const startTime = timeToUnix(winner.final_start);
        const endTime = timeToUnix(winner.final_end);
        // Check if the transaction's timestamp and sender match the winner's details
        if (
          (transactionTime >= startTime &&
            transactionTime <= endTime &&
            transaction.sender === winner.final_winner) ||
          transaction.data.toLowerCase().includes("mvp")
        ) {
          if (!transaction.data.toLowerCase().includes("mvp")) {
            transaction.result = winner.result;
          }
          return true;
        }
      }

      return false;
    });
  }

  async function getTransaction() {
    //devnet-api.multiversx.com/accounts/erd1qqqqqqqqqqqqqpgq4h7lruxkrwfg7zknmsn5xsuu937y5cyr3agsu7l9yn/transactions?from=0&size=10&status=success&withScResults=true&withUsername=true

    await axios
      .get(
        `${erd.providers.api.url}/accounts/${contract_address}/transactions?from=0&size=20&status=success&after=1688212457&withScResults=true&withUsername=true`
      )
      .then((res) => {
        const processedTransactions = res.data
          .map(processTransaction)
          .filter((x: any) => x);
        console.log("processedTransactions: ", processedTransactions);
        const winners = res.data
          .map(processWinningAddresses)
          .filter((x: any) => x);
        console.log("Winners: ", winners);
        const finalTransactions = filterTransactions(
          winners,
          processedTransactions
        );
        finalTransactions.forEach((tx: any) => {
          tx.sender =
            tx.name.length >= 1
              ? tx.name
              : tx.sender.slice(0, 4) + "..." + tx.sender.slice(-4);
        });
        console.log("finalTransactions: ", finalTransactions);
        transactions.value = finalTransactions;
      })
      .catch((err) => console.log(err));
  }

  async function setMaze(data: any) {
    if (account.logged() && account.address !== undefined) {
      sender_account.value = await fetchAccount();
      const networkConfig = await erd.proxy.getNetworkConfig();
      const payload = new TransactionPayload(data);
      const tx = new Transaction({
        data: payload,
        gasLimit: 120000000,
        receiver: new Address(contract_address),
        value: TokenPayment.egldFromAmount(0),
        chainID: networkConfig.ChainID,
        sender: sender_account.value!.address,
      });

      console.log(sender_account.value!.address);
      console.log("tx:");
      console.log(tx);
      console.log(tx.getData().toString());
      console.log("THIS IS THE DATA");
      console.log(data);
      tx.setNonce(sender_account.value!.getNonceThenIncrement());
      /*const signed_tx = await erd.providers.currentProvider.signTransaction(tx);
          console.log(signed_tx);*/
      await erd.providers
        .signAndSend(tx)
        .then((result: Transaction) => {
          transactionUrl.value = erd.explorerTransactionUrl(result);
          initTxToaster();
          console.log(transactionUrl.value);
          const res = erd.providers.transactionResult(result);
          return res;
        })
        .catch((error: Error) => {
          console.error(error);
          transactionResult.value = error;
        });
      return { sessionId: null, error: "tx invalid" };
    }
  }

  async function mvpClaim() {
    if (account.logged() && account.address !== undefined) {
      sender_account.value = await fetchAccount();
      const networkConfig = await erd.proxy.getNetworkConfig();
      const payload = new TransactionPayload("mvpClaim");
      const tx = new Transaction({
        data: payload,
        gasLimit: 60000000,
        receiver: new Address(contract_address),
        value: TokenPayment.egldFromAmount(0),
        chainID: networkConfig.ChainID,
        sender: sender_account.value!.address,
      });
      tx.setNonce(sender_account.value!.getNonceThenIncrement());
      /*const signed_tx = await erd.providers.currentProvider.signTransaction(tx);
          console.log(signed_tx);*/
      await erd.providers
        .signAndSend(tx)
        .then((result: Transaction) => {
          transactionUrl.value = erd.explorerTransactionUrl(result);
          initTxToaster();
          console.log(transactionUrl.value);
          const res = erd.providers.transactionResult(result);
          return res;
        })
        .catch((error: Error) => {
          console.error(error);
          transactionResult.value = error;
        });
      return { sessionId: null, error: "tx invalid" };
    }
  }

  async function makeMove(move: any, jumpAmount: any) {
    if (account.logged() && account.address !== undefined) {
      let user_move = move.toLowerCase();
      if (user_move === "up") {
        user_move = 1;
      } else if (user_move === "right") {
        user_move = 2;
      } else if (user_move === "down") {
        user_move = 3;
      } else if (user_move === "left") {
        user_move = 4;
      }
      sender_account.value = await fetchAccount();
      // Put info on stake modal that only 50 at a time can be staked.

      //const gasLimit = 3.84217 * 10 ** 6 + 3.5707 * 10 ** 6 * tokens.length;
      const gasLimit = 60_000_000;
      //const address = new AddressValue(new Address("erd1d285tah6k8lpj6gak3ngf3wswpf5ehas5pxtnkyem4yx6uuqw7zquehq2g"));
      const amount_to_pay = payment_token.value.amount * jumpAmount;
      const payment = TokenPayment.fungibleFromAmount(
        payment_token.value.ticker,
        amount_to_pay,
        18 //decimal points
      );
      const networkConfig = await erd.proxy.getNetworkConfig();
      const interaction = contract
        .value!.methodsExplicit.addMove([new U64Value(user_move)])
        .withValue(0)
        .withGasLimit(gasLimit)
        .withChainID(networkConfig.ChainID)
        .withSingleESDTTransfer(payment);
      const tx_info = interaction.buildTransaction();
      const tx = new Transaction({
        data: tx_info.getData(),
        gasLimit: tx_info.getGasLimit(),
        receiver: new Address(contract_address),
        value: TokenPayment.egldFromAmount(0),
        chainID: networkConfig.ChainID,
        sender: sender_account.value!.address,
      });
      console.log(sender_account.value!.address);
      console.log("tx:");
      console.log(tx);
      console.log(tx.getData().toString());
      tx.setNonce(sender_account.value!.getNonceThenIncrement());
      /*const signed_tx = await erd.providers.currentProvider.signTransaction(tx);
      console.log(signed_tx);*/
      await erd.providers
        .signAndSend(tx)
        .then((result: Transaction) => {
          transactionUrl.value = erd.explorerTransactionUrl(result);
          initTxToaster();
          console.log(transactionUrl.value);
          const res = erd.providers.transactionResult(result);
        })
        .catch(async (error: Error) => {
          console.error(error);
          console.log(error);
          if (error.message.includes("No matching key")) {
            toast_info.value = {
              title: "Wallet connection lost.",
              message: "Please reconnect.",
              status: null,
              link: null,
            };
            toast_id.value = toast(Msg, {
              autoClose: 5000,
              closeOnClick: true,
              closeButton: true,
              type: "warning",
              isLoading: false,
              position: toast.POSITION.BOTTOM_RIGHT,
            });
            await sleep(5000);
            wipe();
            erd.logout();
          }
        });
    }
  }

  // Staked nfts
  return {
    transactionState,
    transactionResult,
    transactionUrl,
    toast_id,
    toast_info,
    maze,
    player,
    payment_token,
    cooldown,
    last_move_timestamp,
    time_remaining,
    collected_keys,
    gameStatus,
    mvp,
    collectedTokens,
    prizes_to_win,
    mvp_claimed,
    transactions,
    claim_test,
    player_moves,
    getTransaction,
    getGeneralData,
    mvpClaim,
    getCollectedTokens,
    getMvp,
    getGameStatus,
    makeMove,
    reset,
    wipe,
    setContract,
    setMaze,
    init,
    initTxToaster,
    getMaze,
    getPlayerPosition,
    getCoolDown,
    getLastMoveTimestamp,
  };
});

export default useMazeStore;
